import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles, Code2, Megaphone } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { luxe, gradientCss, PILLARS } from '@/lib/luxe';
import { GlassCard, LuxeButton, Field, Input, Spinner } from '@/components/bits';

function Hero() {
  return (
    <div className="hidden lg:flex flex-col justify-center px-14 relative overflow-hidden" style={{ flex: 1 }}>
      <div className="relative z-10 max-w-lg">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8" style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.4)' }}>
          <Sparkles size={16} style={{ color: luxe.gold }} />
          <span className="text-xs font-bold uppercase tracking-widest gold-text">Premium 5D Studio</span>
        </div>
        <h1 className="font-display text-6xl font-black leading-[1.05]" style={{ color: luxe.text, letterSpacing: -1 }}>
          One Studio.<br /><span className="blue-text">Two</span> <span className="gold-text">Powers.</span>
        </h1>
        <p className="mt-6 text-lg" style={{ color: luxe.textMid }}>
          Build software with AI coding agents and run autonomous AI marketing — all inside MYRA AI Studio.
        </p>
        <div className="mt-10 space-y-3">
          {Object.values(PILLARS).map((p) => {
            const Icon = { Code2, Megaphone, Sparkles }[p.icon];
            return (
              <div key={p.key} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: gradientCss(p.grad), boxShadow: `0 8px 20px ${p.glow}` }}>
                  <Icon size={18} color={p.key === 'marketing' ? '#3A2C05' : '#fff'} />
                </div>
                <div>
                  <div className="font-bold" style={{ color: luxe.text }}>{p.name}</div>
                  <div className="text-xs" style={{ color: luxe.muted }}>{p.tagline}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success('Welcome back to MyRaai Studio!');
      navigate('/');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen flex" style={{ zIndex: 1 }}>
      <Hero />
      <div className="flex-1 flex items-center justify-center p-6">
        <GlassCard className="w-full max-w-md p-8 float-in" data-testid="login-card">
          <div className="flex items-center gap-3 mb-1">
            <img src="/myra-logo.png" alt="MYRA AI" className="w-12 h-12 rounded-full object-cover" style={{ boxShadow: '0 0 24px rgba(20,125,255,0.3)' }} />
            <div className="font-display text-2xl font-black" style={{ color: luxe.text }}>MYRA <span className="gold-text">AI Studio</span></div>
          </div>
          <p className="mb-7 mt-3 text-sm" style={{ color: luxe.muted }}>Sign in to your AI command center.</p>
          <form onSubmit={submit}>
            <Field label="Email">
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@brand.com" />
            </Field>
            <Field label="Password">
              <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </Field>
            <LuxeButton data-testid="login-submit" type="submit" full disabled={busy} className="mt-2">
              {busy ? <span className="flex items-center justify-center gap-2"><Spinner color="#3A2C05" /> Signing in…</span> : 'Sign In'}
            </LuxeButton>
          </form>
          <div className="mt-6 text-center text-sm" style={{ color: luxe.muted }}>
            New here?{' '}
            <Link to="/signup" data-testid="go-signup" className="font-bold" style={{ color: luxe.blue }}>Create an account</Link>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
